import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";

/** How long the confirm must be held (T16). */
export const SIGN_OUT_HOLD_MS = 1500;

/**
 * SIGN-OUT BEHIND A HOLD (T16).
 *
 * A wall tablet is brushed by elbows, trays and cloths all evening, and one
 * stray tap on sign-out used to leave the pass looking at a login form in the
 * middle of service. The header's sign-out now opens this sheet; its confirm
 * signs out only after a 1.5 s press-and-hold (pointer, or Enter / Space held
 * on the keyboard), with the button filling as it goes. A tap does nothing;
 * letting go early cancels; «إلغاء» closes. `Screen` itself is unchanged.
 */
export function KitchenSignOutSheet({
  isOpen,
  onClose,
  onConfirm,
  isLoggingOut = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoggingOut?: boolean;
}) {
  const t = useTranslate();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      title={t((m) => m.kitchen.signOutTitle)}
      closeLabel={t((m) => m.common.close)}
      data-testid="kitchen-signout-sheet"
      footerActions={
        <Button variant="outline" size="lg" data-testid="kitchen-signout-cancel" onClick={onClose}>
          {t((m) => m.common.cancel)}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <p data-ui="kitchen-signout-body">{t((m) => m.kitchen.signOutBody)}</p>
        {isOpen ? (
          <HoldButton
            label={t((m) => m.kitchen.signOutHold)}
            disabled={isLoggingOut}
            onComplete={onConfirm}
          />
        ) : null}
      </div>
    </Modal>
  );
}

function HoldButton({
  label,
  disabled,
  onComplete,
}: {
  label: string;
  disabled: boolean;
  onComplete: () => void;
}) {
  const [holding, setHolding] = useState(false);
  const timer = useRef<number | null>(null);
  const complete = useRef(onComplete);
  useEffect(() => {
    complete.current = onComplete;
  }, [onComplete]);

  const cancel = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    setHolding(false);
  }, []);
  const begin = useCallback(() => {
    if (disabled || timer.current !== null) return;
    setHolding(true);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      setHolding(false);
      complete.current();
    }, SIGN_OUT_HOLD_MS);
  }, [disabled]);
  // Closing the sheet mid-hold must not sign anyone out afterwards.
  useEffect(() => cancel, [cancel]);

  return (
    <button
      type="button"
      data-ui="kitchen-signout-hold"
      data-testid="kitchen-signout-hold"
      data-holding={holding ? "" : undefined}
      disabled={disabled}
      aria-label={label}
      style={{ ["--hold-ms" as string]: `${SIGN_OUT_HOLD_MS}ms` }}
      onPointerDown={(event) => {
        // Only the primary button (a finger, a pen, a left click) starts it.
        if (event.button > 0) return;
        begin();
      }}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        // A held key repeats; the hold is measured from the first press, and
        // the browser's own Enter/Space "click" must not fire anything either.
        event.preventDefault();
        if (!event.repeat) begin();
      }}
      onKeyUp={(event) => {
        if (event.key === "Enter" || event.key === " ") cancel();
      }}
      onBlur={cancel}
      onContextMenu={(event) => event.preventDefault()}
      className="relative flex w-full items-center justify-center overflow-hidden"
    >
      <span aria-hidden="true" data-ui="kitchen-signout-hold-fill" />
      <span className="relative">{label}</span>
    </button>
  );
}
